/** @jsxImportSource @emotion/react */
import { ReactNode, createContext, useState, useCallback, useMemo, useContext, useEffect, CSSProperties } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { PopperPlacementType } from "@material-ui/core";
import { keys, values } from "lodash";

import { Popover } from "../components";
import { TooltipId } from "../types";

export interface Props {
  id: string;
  anchorEl: HTMLElement | Element | null;
  placement?: PopperPlacementType;
  title: string;
  content: string;
  style?: CSSProperties;
  onClose?: () => void;
}

interface ElementInfo {
  el: HTMLElement | Element;
  placement?: PopperPlacementType;
  title: string;
  content: string;
  style?: CSSProperties;
  onClose?: () => void;
}

type ContextProps = [(args: Props[]) => void, (id: TooltipId) => void];

interface ProviderProps {
  children: ReactNode;
}

const TooltipContext = createContext<Partial<ContextProps>>([]);

const TooltipProvider = ({ children }: ProviderProps): JSX.Element => {
  const intl = useIntl();
  const { listen } = useHistory();
  const [tooltipElements, setTooltipElements] = useState<Record<string, ElementInfo>>({});

  const showTooltip = useCallback((items: Props[] = []) => {
    items.forEach((item) => {
      const { id, anchorEl, title, content, placement, style, onClose } = item;
      if (anchorEl) {
        setTooltipElements((prev: Record<string, ElementInfo>) => ({
          ...prev,
          [id]: {
            el: anchorEl,
            title,
            content,
            style,
            ...(placement && { placement }),
            ...(onClose && { onClose }),
          },
        }));
      }
    });
  }, []);

  const hideTooltip = useCallback((id: TooltipId) => {
    if (values(TooltipId).find((itemId) => itemId === id)) {
      setTooltipElements((prev) => {
        const copyPrev = { ...prev };
        delete copyPrev[id];
        return copyPrev;
      });
    }
  }, []);
  const value: ContextProps = useMemo(() => [showTooltip, hideTooltip], [showTooltip, hideTooltip]);

  useEffect(() => {
    const unlisten = listen(() => {
      if (tooltipElements) setTooltipElements({});
    });

    return (): void => unlisten();
  }, [listen, tooltipElements, setTooltipElements]);

  return (
    <TooltipContext.Provider value={value}>
      {keys(tooltipElements).map((id) => {
        const { el, placement, title, content, style, onClose } = tooltipElements[id];
        return (
          <Popover
            key={id}
            id={id}
            open={Boolean(el)}
            anchorEl={el}
            title={title}
            content={content}
            placement={placement}
            style={style}
            actions={[
              {
                primary: true,
                label: intl.formatMessage({ id: "btn.ok" }),
                onClick: () => {
                  setTooltipElements((prev) => {
                    const copyPrev = { ...prev };
                    delete copyPrev[id];
                    return copyPrev;
                  });
                  onClose?.();
                },
              },
            ]}
          />
        );
      })}

      {children}
    </TooltipContext.Provider>
  );
};

const useTooltip = (): ContextProps => useContext(TooltipContext) as ContextProps;

export { TooltipProvider, useTooltip };
